import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Collapse } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import SocialLinkCard from './SocialCard';
const PLATFORM_OPTIONS = [
    { value: 'WEBSITE', label: 'Website' },
    { value: 'GOOGLE_PAGE', label: 'Google Business' },
    { value: 'FACEBOOK', label: 'Facebook' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'TWITTER', label: 'Twitter' },
    { value: 'YELP', label: 'Yelp' },
    { value: 'OTHER', label: 'Other' },
];

function AddLinkForm({ clinicId, onSuccess, onCancel }) {
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = (data) => {
        setIsSubmitting(true);
        clinicService
            .updateSocialLinks(clinicId, data)
            .then(() => {
                toast.success('Social link added', { position: toast.POSITION.BOTTOM_RIGHT });
                reset();
                onSuccess?.();
            })
            .catch(() => {
                toast.error('Failed to add link', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="clinic-social-add-form">
            <div className="notifications-form-label mb-3">
                <i className="bi bi-plus-circle me-1"></i>
                Add New Social Link
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label htmlFor="socialType" className="notifications-form-label">
                            Platform <span className="text-danger">*</span>
                        </label>
                        <select
                            id="socialType"
                            className={`form-select form-select-sm ${errors.type ? 'is-invalid' : ''}`}
                            disabled={isSubmitting}
                            {...register('type', { required: 'Platform is required' })}
                        >
                            <option value="">Select platform</option>
                            {PLATFORM_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                    </div>
                    <div className="col-md-8">
                        <label htmlFor="socialLink" className="notifications-form-label">
                            URL <span className="text-danger">*</span>
                        </label>
                        <input
                            type="url"
                            id="socialLink"
                            className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                            placeholder="https://example.com"
                            disabled={isSubmitting}
                            {...register('link', {
                                required: 'URL is required',
                                pattern: {
                                    value: /^https?:\/\/.+/,
                                    message: 'Must start with http:// or https://',
                                },
                            })}
                        />
                        {errors.link && <div className="invalid-feedback">{errors.link.message}</div>}
                        <input type="hidden" {...register('clinicId')} value={clinicId} />
                    </div>
                </div>
                <div className="clinic-social-form-actions">
                    {onCancel && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </button>
                    )}
                    <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Saving…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-floppy me-1"></i>
                                Save Link
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

function SocialTab() {
    const { clinicId } = useParams();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    useEffect(() => {
        fetchLinks();
    }, [clinicId]);

    const fetchLinks = () => {
        setLoading(true);
        setError(null);
        clinicService
            .getSocialLinks(clinicId)
            .then((res) => setLinks(res.data || []))
            .catch(() => {
                setError('Failed to load social links.');
                setLinks([]);
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="clinic-social-panel">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-social__title mb-1">
                        <i className="bi bi-share-fill text-primary me-1"></i>
                        Social Links Management
                    </h5>
                    <p className="text-muted small mb-0">Manage website and social profile links for this clinic.</p>
                </div>
                <button
                    type="button"
                    className={`btn btn-sm ${showAddForm ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
                    {showAddForm ? 'Cancel' : 'Add Link'}
                </button>
            </div>

            {error && !loading && (
                <div className="alert alert-warning border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </div>
            )}

            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-link-45deg"></i>
                        Social Links
                        {!loading && (
                            <span className="text-muted small fw-normal">
                                ({links.length} link{links.length !== 1 ? 's' : ''})
                            </span>
                        )}
                    </span>
                </div>

                <div className="notifications-panel__body">
                    <Collapse in={showAddForm}>
                        <div className="mb-3">
                            <AddLinkForm
                                clinicId={clinicId}
                                onSuccess={() => {
                                    setShowAddForm(false);
                                    fetchLinks();
                                }}
                                onCancel={() => setShowAddForm(false)}
                            />
                        </div>
                    </Collapse>

                    {loading ? (
                        <Loading />
                    ) : links.length > 0 ? (
                        <div className="clinic-social-link-grid">
                            {links.map((link) => (
                                <SocialLinkCard key={link.id} linkitem={link} onUpdate={fetchLinks} />
                            ))}
                        </div>
                    ) : (
                        <div className="clinic-social-empty">
                            <i className="bi bi-share"></i>
                            No social links configured.
                            {!showAddForm && (
                                <div className="mt-3">
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowAddForm(true)}>
                                        <i className="bi bi-plus-lg me-1"></i>
                                        Add First Link
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SocialTab;
